// Shopify integration configuration.
// Live store: shop.marineforceled.com. Variant IDs for the seven official
// systems live with their prices in data/pricing.ts (single source of truth).
// Checkout handoff uses Shopify cart permalinks: https://{domain}/cart/{variant}:{qty},{variant}:{qty}

export const shopifyConfig = {
  storeDomain: 'shop.marineforceled.com',
  discountMethod: 'bundle-variants' as 'automatic' | 'functions' | 'bundle-variants',
  /** Query params forwarded from the funnel to Shopify for attribution. */
  attributionParams: ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'fbclid', 'gclid', 'ttclid'],
};

/**
 * Shopify Analytics for the funnel (Headless channel).
 * `storefrontToken` is the PUBLIC Storefront API access token from Shopify Admin →
 * Sales channels → Headless → M1 Funnel (safe to ship in the browser; never put the private token here).
 * `shopId` is resolved automatically from the token; you may also paste the numeric Shop ID directly.
 * Shared visitor cookies are set on the root domain so funnel and checkout sessions join up.
 */
export const shopifyAnalyticsConfig = {
  storefrontToken: '8aecc2d6841ffef9d04601008c570b38',
  storefrontApiVersion: '2024-10',
  shopId: '',
  /** Headless → your storefront → "Storefront ID" (numbers only). Ties funnel events to the Headless channel. */
  storefrontId: '398587',
  cookieDomain: '.marineforceled.com',
};

/** Official Shopify payment icon set (the same artwork Shopify themes use). */
const PAYMENT_ICON_BASE = 'https://cdn.jsdelivr.net/gh/activemerchant/payment_icons@master/app/assets/images/payment_icons';

export type PaymentMethod = {
  id: string;
  label: string;
  icon: string;
  /** Set to false to hide a method that is not enabled in Shopify checkout. */
  enabled: boolean;
};

// Payment methods shown near checkout CTAs. Keep this list identical to
// Shopify Admin → Settings → Payments. Toggle `enabled` to match.
export const paymentMethods: PaymentMethod[] = [
  { id: 'shopify_pay', label: 'Shop Pay', icon: `${PAYMENT_ICON_BASE}/shopify_pay.svg`, enabled: true },
  { id: 'apple_pay', label: 'Apple Pay', icon: `${PAYMENT_ICON_BASE}/apple_pay.svg`, enabled: true },
  { id: 'google_pay', label: 'Google Pay', icon: `${PAYMENT_ICON_BASE}/google_pay.svg`, enabled: true },
  { id: 'paypal', label: 'PayPal', icon: `${PAYMENT_ICON_BASE}/paypal.svg`, enabled: true },
  { id: 'amazon', label: 'Amazon Pay', icon: `${PAYMENT_ICON_BASE}/amazon.svg`, enabled: true },
  { id: 'visa', label: 'Visa', icon: `${PAYMENT_ICON_BASE}/visa.svg`, enabled: true },
  { id: 'master', label: 'Mastercard', icon: `${PAYMENT_ICON_BASE}/master.svg`, enabled: true },
  { id: 'american_express', label: 'American Express', icon: `${PAYMENT_ICON_BASE}/american_express.svg`, enabled: true },
  { id: 'discover', label: 'Discover', icon: `${PAYMENT_ICON_BASE}/discover.svg`, enabled: true },
  { id: 'diners_club', label: 'Diners Club', icon: `${PAYMENT_ICON_BASE}/diners_club.svg`, enabled: true },
  { id: 'jcb', label: 'JCB', icon: `${PAYMENT_ICON_BASE}/jcb.svg`, enabled: true },
];

/** Customer-facing checkout security points (all true of the hosted checkout). */
export const checkoutTrust = {
  points: ['256-bit SSL encryption', 'PCI DSS compliant payments', 'We never store your card details'],
};

export type ShippingCarrier = { id: string; label: string; logo: string };

/** Carriers used to fulfil orders. */
export const shippingCarriers: ShippingCarrier[] = [
  {
    id: 'usps',
    label: 'USPS — United States Postal Service',
    logo: 'https://commons.wikimedia.org/wiki/Special:FilePath/USPS_-_Color_Logo.svg',
  },
];

export const trackingConfig = {
  metaPixelId: '1240164748313404',
  gtmId: '',
  conversionsApiEndpoint: '',
};
